import { CASStatus } from 'shared'
import { RevisionActions } from './components/RevisionActions'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission } from 'shared'
import { useEffect, useState } from 'react'
import { GroupReviewerActions } from './components/GroupReviewerActions'
import { REReviewerActions } from './components/REReviewerActions'
import { RouterActions } from './components/RouterActions'
import { CIMSModal } from 'components/CIMSModal'
import {
  ConstructionAdminSubmittal,
  useReReviewCasMutation,
  useGroupReviewCasMutation,
  useRequestCasRevisionsMutation,
  useReviseCaSubmittalMutation,
  useRouteCasToGroupReviewMutation,
  useRouteCasToReReviewMutation,
} from 'schema'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useSubmittalTrackerContext } from '../../../../SubmittalTrackerContext'
import { toMoment } from 'utils/toMoment'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useForm } from 'antd/lib/form/Form'
import { errorHandler } from 'hooks/errorHandler'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { InitialValues } from './types'

export const ActionModal: React.VFC = () => {
  const { refetch, actionModal } = useSubmittalTrackerContext()
  const { state, toggle, visible } = actionModal
  const canRevise = useCheckPermission(
    Permission.ReviseConstructionAdminSubmittal
  )
  const [revise] = useReviseCaSubmittalMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ reviseConstructionAdminSubmittal: { submittal } }) =>
      done(submittal),
  })

  const canRoute = useCheckPermission(
    Permission.RouteConstructionAdminSubmittal
  )
  const [routeToGroupReview] = useRouteCasToGroupReviewMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      routeConstructionAdminSubmittalToGroupReview: { submittal },
    }) => done(submittal),
  })
  const [routeToReReview] = useRouteCasToReReviewMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      routeConstructionAdminSubmittalToREReview: { submittal },
    }) => done(submittal),
  })

  const canGroupReview = useCheckPermission(
    Permission.GroupReviewConstructionAdminSubmittal
  )
  const [groupReview] = useGroupReviewCasMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ groupReviewConstructionAdminSubmittal: { submittal } }) =>
      done(submittal),
  })

  const canGiveDisposition = useCheckPermission(
    Permission.GiveDispositionConstructionAdminSubmittal
  )
  const [reReview] = useReReviewCasMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ reReviewConstructionAdminSubmittal: { submittal } }) =>
      done(submittal),
  })
  const [requestRevisions] = useRequestCasRevisionsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      requestConstructionAdminSubmittalRevisions: { submittal },
    }) => done(submittal),
  })

  const [loading, setLoading] = useState(false)
  function done({
    sharePointFolderName,
    statusId,
  }: Pick<ConstructionAdminSubmittal, 'sharePointFolderName' | 'statusId'>) {
    simpleSuccessNotification(
      `${sharePointFolderName} ${
        statusId === CASStatus.Closed.id || statusId === CASStatus.Unlocked.id
          ? ''
          : 'sent to '
      }${CASStatus.fromIdOrFail(statusId).name}`
    )
    setLoading(false)
  }
  const [form] = useForm()

  useEffect(() => {
    // @ts-expect-error don't set action. let each component set it
    form.setFieldsValue({
      contractualReviewDate: state?.contractualReviewDate
        ? toMoment(state.contractualReviewDate)
        : null,
      dispositionType: state?.dispositionType
        ? ({
            label: state.dispositionType.name,
            value: state.dispositionType.id,
            tooltip: state.dispositionType.description,
            deleted: state.dispositionType.deleted,
          } as LabeledValueType)
        : null,
      packageName: state?.packageName,
      packageNumber: state?.packageNumber,
      quantity: state?.quantity,
      requestedReviewDate: state?.requestedReviewDate
        ? toMoment(state.requestedReviewDate)
        : null,
      routingGroups: toLabeledValues(state?.casToCASRoutingGroups, {
        type: 'callback',
        callback: ({ routingGroup: { id, name, description } }) => ({
          label: name,
          value: id,
          tooltip: description,
        }),
      }),
      type: state?.type
        ? ({
            label: state.type.name,
            value: state.type.id,
            tooltip: state.type.description,
          } as LabeledValueType)
        : null,
      subtype: state?.subtype
        ? ({
            label: state.subtype.name,
            value: state.subtype.id,
            tooltip: state.subtype.description,
          } as LabeledValueType)
        : null,
      units: state?.units,
      externalLinks: state?.externalLinks?.map(({ href, label }) => ({
        href,
        label,
      })),
      comment: null,
    } as InitialValues)
    if (!state) form.setFieldValue('action', undefined)
  }, [form, state])
  if (!state) return null
  return (
    <CIMSModal
      externalForm={form}
      disabled={loading}
      loading={loading}
      title={CASStatus.fromIdOrFail(state.statusId).name}
      visible={visible}
      onCancel={() => toggle(null)}
      onOk={async (values: InitialValues) => {
        setLoading(true)
        const id = state.id
        if (values.action === 'groupReview') {
          await groupReview({
            variables: { input: { id, comment: values.comment } },
          })
        } else if (values.action === 'reReview') {
          const { dispositionType } = values
          await reReview({
            variables: {
              input: {
                id,
                dispositionTypeId: dispositionType?.value?.toString(),
                comment: values.comment,
              },
            },
          })
        } else if (values.action === 'requestRevisions') {
          await requestRevisions({
            variables: { input: { id, comment: values.comment } },
          })
        } else if (values.action === 'revise') {
          const { quantity, requestedReviewDate, type, units, subtype } = values
          await revise({
            variables: {
              input: {
                id,
                comment: values.comment,
                quantity,
                requestedReviewDate: requestedReviewDate.toISOString(),
                typeId: type.value.toString(),
                subtypeId: subtype?.value?.toString(),
                units,
              },
            },
          })
        } else if (values.action === 'route') {
          const { contractualReviewDate, routingGroups, externalLinks } = values
          await routeToGroupReview({
            variables: {
              input: {
                id,
                comment: values.comment,
                contractualReviewDate: contractualReviewDate.toISOString(),
                routingGroupIds: routingGroups.map(({ value }) =>
                  value.toString()
                ),
                externalLinks,
              },
            },
          })
        } else if (values.action === 'groupReviewAsRouter') {
          const { contractualReviewDate, externalLinks } = values
          await routeToReReview({
            variables: {
              input: {
                id,
                comment: values.comment,
                contractualReviewDate: contractualReviewDate.toISOString(),
                externalLinks,
              },
            },
          })
        } else if (values.action === 'reReviewAsRouter') {
          const { contractualReviewDate, dispositionType } = values
          await reReview({
            variables: {
              input: {
                id,
                comment: values.comment,
                contractualReviewDate: contractualReviewDate.toISOString(),
                dispositionTypeId: dispositionType.value.toString(),
              },
            },
          })
        } else errorHandler(`Invalid action type: ${values.action}`)
        setLoading(false)
        refetch()
        return toggle(null)
      }}
    >
      {canRevise && state.statusId === CASStatus.RevisionsRequested.id && (
        <RevisionActions sharePointFolderName={state.sharePointFolderName} />
      )}
      {canRoute && state.statusId === CASStatus.RouterReview.id && (
        <RouterActions form={form} />
      )}
      {canGroupReview && state.statusId === CASStatus.GroupReview.id && (
        <GroupReviewerActions />
      )}
      {canGiveDisposition && state.statusId === CASStatus.REReview.id && (
        <REReviewerActions form={form} />
      )}
    </CIMSModal>
  )
}
